import { CHARACTER_STATE_PRIORITY, isCharacterState } from './character-state';
import type { CharacterRuntimeState } from './runtime-state';
import type { AcceptedTransition, TransitionRequest } from './transition';

interface SequencedTransitionRequest extends TransitionRequest {
  readonly sequence: number;
}

export class CharacterStateMachine {
  private pending: SequencedTransitionRequest[] = [];
  private nextSequence = 0;

  public constructor(private readonly runtime: CharacterRuntimeState) {}

  public request(request: TransitionRequest): void {
    if (!isCharacterState(request.target)) throw new RangeError(`Unknown character state: ${String(request.target)}`);
    if (this.runtime.state === 'KO' && request.target !== 'KO') {
      throw new Error('KO is terminal until battle reset.');
    }
    this.pending.push({ ...request, sequence: this.nextSequence });
    this.nextSequence += 1;
  }

  public resolve(): AcceptedTransition | null {
    let selected: SequencedTransitionRequest | undefined;
    for (const request of this.pending) {
      if (selected === undefined
        || CHARACTER_STATE_PRIORITY[request.target] > CHARACTER_STATE_PRIORITY[selected.target]
        || (CHARACTER_STATE_PRIORITY[request.target] === CHARACTER_STATE_PRIORITY[selected.target]
          && request.sequence < selected.sequence)) {
        selected = request;
      }
    }
    this.pending = [];
    if (selected === undefined || selected.target === this.runtime.state) return null;
    const transition: AcceptedTransition = Object.freeze({
      from: this.runtime.state,
      target: selected.target,
      reason: selected.reason,
    });
    this.runtime.state = selected.target;
    this.runtime.stateFrame = 0;
    this.runtime.lastTransitionReason = selected.reason;
    return transition;
  }

  public finishLogicalFrame(transitionAccepted: boolean): void {
    if (!transitionAccepted) this.runtime.stateFrame += 1;
  }

  public clearPending(): void {
    this.pending = [];
  }

  public cloneFor(runtime: CharacterRuntimeState): CharacterStateMachine {
    const clone = new CharacterStateMachine(runtime);
    clone.pending = this.pending.map((request) => ({ ...request }));
    clone.nextSequence = this.nextSequence;
    return clone;
  }

  public get pendingCount(): number {
    return this.pending.length;
  }
}
